// src/services/NotificationService.js
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { firebaseApp } from '../firebase/config';

export const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const isStandaloneDisplay = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const isPushSupported = async () => {
  if (!('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
};

// Requests permission and, if granted, registers for a push token using the
// already-registered Workbox service worker (src/service-worker.js), rather
// than letting Firebase register its own separate service worker file.
export const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined' || !(await isPushSupported())) {
    return { status: 'unsupported' };
  }

  if (Notification.permission === 'denied') {
    return { status: 'denied' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { status: permission };
  }

  const registration = await navigator.serviceWorker.ready;
  const messaging = getMessaging(firebaseApp);
  const token = await getToken(messaging, {
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  return { status: 'granted', token };
};

// Fires when a push arrives while the app is open and focused - the service
// worker's onBackgroundMessage handler doesn't run in that case.
export const listenForForegroundMessages = async (callback) => {
  if (!(await isPushSupported())) return;
  const messaging = getMessaging(firebaseApp);
  onMessage(messaging, callback);
};
